import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { deleteComment } from '../../../api/comments';

import { generateCommentCIDKey } from '../../../components/Comments/utils/generateCommentCIDKey';
import {
  checkAndSetLoadingWithId,
  handleRequestRejection,
  onMatchedRequest,
} from '../../../utils/asyncThunkRequest';
import { generateItemCIDKey } from '../../../components/Items/utils/generateItemCIDKey';
import { REQUEST } from '../../../constants/request';
import { mergeCommentWithUserData } from '../../../utils/comments';

export const removeComment = createAsyncThunk(
  'comments/removeComment',
  async (commentData, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const [commentResponse, commentUserDaraResponse] = await deleteComment(
        commentData,
        {
          cancelToken: source.token,
        },
      );
      return mergeCommentWithUserData(commentResponse, commentUserDaraResponse);
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
  {
    condition: ({ path }, { getState }) => {
      const state = getState();

      return (
        state.comments.comments.comment.actions.delete.request[
          generateCommentCIDKey(path)
        ]?.loading !== REQUEST.pending
      );
    },
  },
);

export const removeCommentExtraReducers = builder => {
  builder
    .addCase(removeComment.pending, (state, action) => {
      const commentCIDKey = generateCommentCIDKey(action.meta.arg.path);
      checkAndSetLoadingWithId(
        state.comments.comment.actions.delete.request,
        action,
        commentCIDKey,
      );
    })
    .addCase(removeComment.fulfilled, (state, action) => {
      const commentCIDKey = generateCommentCIDKey(action.meta.arg.path);
      const itemCIDKey = generateItemCIDKey(action.meta.arg.path);
      const newAllIds = [];
      const newAllIdsByItem = [];
      const newAllIdsByParent = [];
      const AllParentIds = [...Object.keys(state.comments.parent.byId)];
      onMatchedRequest(
        state.comments.comment.actions.delete.request[commentCIDKey],
        action,
        () => {
          if (state.comments.comment.byId[commentCIDKey].metadata.replies > 0) {
            // TODO: When the backend is ready to be replaced with:
            // state.comments.comment.byId[commentCIDKey] = action.payload;
            state.comments.comment.byId[commentCIDKey].metadata = {
              ...state.comments.comment.byId[commentCIDKey].metadata,
              createdBy: 'del',
              deleteAt: new Date().toString(),
            };
          } else {
            delete state.comments.comment.byId[commentCIDKey];
            newAllIds.push(
              ...state.comments.comment.allIds.filter(
                commentCID => commentCID !== commentCIDKey,
              ),
            );
            state.comments.comment.allIds = newAllIds;
            if (state.comments.item.byId[itemCIDKey].includes(commentCIDKey)) {
              newAllIdsByItem.push(
                ...state.comments.item.byId[itemCIDKey].filter(
                  commentCID => commentCID !== commentCIDKey,
                ),
              );
              state.comments.item.byId[itemCIDKey] = newAllIdsByItem;
            }
            AllParentIds.forEach(parentCIDKey => {
              if (
                state.comments.parent.byId[parentCIDKey].includes(commentCIDKey)
              ) {
                newAllIdsByParent.push(
                  ...state.comments.parent.byId[parentCIDKey].filter(
                    commentCID => commentCID !== commentCIDKey,
                  ),
                );
                state.comments.parent.byId[parentCIDKey] = newAllIdsByParent;
              }
            });
          }
          state.comments.removeCommentId = commentCIDKey;
        },
      );
    })
    .addCase(removeComment.rejected, (state, action) => {
      const commentCIDKey = generateCommentCIDKey(action.meta.arg.path);
      handleRequestRejection(
        state.comments.comment.actions.delete.request[commentCIDKey],
        action,
      );
    });
};
